import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Row, Col, Table, Card } from 'react-bootstrap';
import { useMutation, useQuery } from '@apollo/client';
import { SUBMIT_QUOTE, GET_PRODUCTS, GET_ADMIN_USERS } from '../../graphql/operations';
import CustomerModal from './CustomerModal';

interface CreateQuoteModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (message: string) => void;
}

const CreateQuoteModal: React.FC<CreateQuoteModalProps> = ({ show, onHide, onSuccess }) => {
  const { data: productsData, loading: productsLoading } = useQuery(GET_PRODUCTS, {
    fetchPolicy: 'network-only',
    skip: !show,
  });

  const [submitQuote, { loading: submitting }] = useMutation(SUBMIT_QUOTE);

  // Items State
  const [items, setItems] = useState<any[]>([]);

  // Current Item Form State
  const [selectedProductId, setSelectedProductId] = useState('');
  const [customItemName, setCustomItemName] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [bannerWidth, setBannerWidth] = useState(1);
  const [bannerHeight, setBannerHeight] = useState(1);
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [unitPrice, setUnitPrice] = useState<number | ''>('');
  const [customDescription, setCustomDescription] = useState('');

  // Customer details
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  const [selectedCustomerId, setSelectedCustomerIdState] = useState<string>('');
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const { data: usersResult } = useQuery(GET_ADMIN_USERS, {
    fetchPolicy: 'network-only',
    skip: !show,
  });
  const customers = React.useMemo(() => 
    (usersResult?.users || []).filter((u: any) => u.role === 'customer'),
    [usersResult]
  );

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (show) {
      setItems([]);
      setSelectedProductId('');
      setCustomItemName('');
      setSelectedOptions({});
      setBannerWidth(1);
      setBannerHeight(1);
      setQuantity(1);
      setUnitPrice('');
      setCustomDescription('');
      
      setSelectedCustomerIdState('');
      setShowAddCustomerModal(false);
      setCustomerName('');
      setCustomerPhone('');
      setCustomerEmail('');
      setErrors({});
      setItemErrors({});
      setSubmitError('');
    }
  }, [show]);

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: value
    }));
  };

  const validateItem = () => {
    const newErrors: Record<string, string> = {};
    if (!selectedProductId) newErrors.product = 'Please select a product catalogue';
    if (selectedProductId === 'custom' && !customItemName.trim()) newErrors.customProduct = 'Product name is required';
    if (!quantity || Number(quantity) <= 0) newErrors.quantity = 'Valid quantity is required';
    
    setItemErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddItem = () => {
    if (!validateItem()) return;

    const selectedProduct = productsData?.products?.find((p: any) => p.productId === selectedProductId);

    const specs = [
      ...Object.entries(selectedOptions).map(([k, v]) => `${v}`),
      customDescription,
      (selectedProductId === 'flex-banners' || selectedProduct?.hasDimensions) ? `Size: ${bannerWidth}x${bannerHeight} ft` : ''
    ].filter(Boolean).join(' | ');

    let finalDesc = specs;
    if (unitPrice && Number(unitPrice) > 0) {
      finalDesc += (finalDesc ? ' | ' : '') + `Unit Price: ₹${unitPrice}`;
    }

    const newItem = {
      product: selectedProduct?.name || customItemName,
      quantity: quantity.toString(),
      size: (selectedProductId === 'flex-banners' || selectedProduct?.hasDimensions) ? `${bannerWidth}x${bannerHeight} ft` : '',
      description: finalDesc,
    };

    setItems([...items, newItem]);

    // Reset item form
    setSelectedProductId('');
    setCustomItemName('');
    setSelectedOptions({});
    setBannerWidth(1);
    setBannerHeight(1);
    setQuantity(1);
    setUnitPrice('');
    setCustomDescription('');
    setItemErrors({});
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const validateSubmit = () => {
    const newErrors: Record<string, string> = {};
    
    if (items.length === 0) {
      setSubmitError('Please add at least one line item to the quote.');
      return false;
    }

    if (!customerName.trim()) newErrors.name = 'Customer name is required';
    if (!customerPhone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(customerPhone.replace(/[\s\-+]/g, '').slice(-10))) {
      newErrors.phone = 'Enter a valid 10-digit number';
    }

    if (customerEmail.trim() && !/\S+@\S+\.\S+/.test(customerEmail)) {
      newErrors.email = 'Enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSubmit()) return;
    setSubmitError('');

    try {
      const { data } = await submitQuote({
        variables: {
          input: {
            items: items.map(item => ({
              product: item.product,
              quantity: item.quantity,
              size: item.size || '',
              description: item.description || ''
            })),
            name: customerName.trim(),
            phone: customerPhone.trim(),
            email: customerEmail.trim() || undefined,
            type: 'walk-in',
          },
        },
      });

      if (data?.submitQuote?.success) {
        onSuccess(data.submitQuote.message || 'Quote request logged successfully!');
        onHide();
      } else {
        setSubmitError(data?.submitQuote?.message || 'Failed to log quote request.');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'An error occurred during submission.');
    }
  };

  const selectedProductObj = productsData?.products?.find((p: any) => p.productId === selectedProductId);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-bottom pb-3" style={{ background: 'linear-gradient(135deg, #f8f9ff, #ffffff)' }}>
        <Modal.Title className="fw-bold text-dark" style={{ fontSize: '18px' }}>
          <i className="fas fa-file-invoice text-primary me-2" />
          Log New Print Quote Request
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ padding: '24px' }}>
          {submitError && (
            <div className="alert alert-danger mb-4 py-2 px-3 rounded-3 border-0" style={{ fontSize: '13px' }}>
              <i className="fas fa-exclamation-triangle me-2" />
              {submitError}
            </div>
          )}

          {/* ADD ITEM SECTION */}
          <Card className="border shadow-sm p-4 rounded-4 mb-4">
            <h5 className="fw-bold text-dark mb-3" style={{ fontSize: '14px' }}>
              <i className="fas fa-cart-plus text-primary me-2"></i>Add Line Item to Quotation
            </h5>
            
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Select Product Catalogue *</Form.Label>
                  {productsLoading ? (
                    <div className="d-flex align-items-center gap-2 py-1 text-muted" style={{ fontSize: '13px' }}>
                      <Spinner size="sm" animation="border" /> Loading...
                    </div>
                  ) : (
                    <Form.Select
                      size="sm"
                      value={selectedProductId}
                      onChange={(e) => {
                        setSelectedProductId(e.target.value);
                        setSelectedOptions({});
                      }}
                      style={{ borderRadius: '8px' }}
                      isInvalid={!!itemErrors.product}
                    >
                      <option value="">-- Select Product --</option>
                      <option value="custom" className="fw-bold text-primary">+ Custom Line Item</option>
                      {productsData?.products?.map((p: any) => (
                        <option key={p.productId} value={p.productId}>{p.name}</option>
                      ))}
                    </Form.Select>
                  )}
                  <Form.Control.Feedback type="invalid">{itemErrors.product}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* DYNAMIC PRODUCT OPTIONS */}
              {selectedProductObj && (
                <>
                  {selectedProductObj.options?.map((opt: any) => {
                    if (opt.type === 'select' || opt.type === 'radio') {
                      return (
                        <Col md={3} key={opt.name}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>{opt.label}</Form.Label>
                            <Form.Select
                              size="sm"
                              value={selectedOptions[opt.name] || ''}
                              onChange={(e) => handleOptionChange(opt.name, e.target.value)}
                              style={{ borderRadius: '8px' }}
                            >
                              <option value="">None</option>
                              {opt.choices?.map((c: any) => (
                                <option key={c.label} value={c.label}>
                                  {c.label} {c.extraCost > 0 ? `(+₹${c.extraCost})` : ''}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      );
                    }
                    return null;
                  })}

                  {/* WIDTH & HEIGHT FOR BANNER */}
                  {(selectedProductId === 'flex-banners' || selectedProductObj.hasDimensions) && (
                    <>
                      <Col md={1.5} xs={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Width (ft)</Form.Label>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={1}
                            value={bannerWidth}
                            onChange={(e) => setBannerWidth(Number(e.target.value) || 1)}
                            style={{ borderRadius: '8px' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={1.5} xs={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Height (ft)</Form.Label>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={1}
                            value={bannerHeight}
                            onChange={(e) => setBannerHeight(Number(e.target.value) || 1)}
                            style={{ borderRadius: '8px' }}
                          />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                </>
              )}

              {/* CUSTOM PRODUCT NAME */}
              {selectedProductId === 'custom' && (
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Custom Product Name *</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={customItemName}
                      onChange={(e) => setCustomItemName(e.target.value)}
                      placeholder="e.g. Custom Flyer"
                      style={{ borderRadius: '8px' }}
                      isInvalid={!!itemErrors.customProduct}
                    />
                    <Form.Control.Feedback type="invalid">{itemErrors.customProduct}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
            </Row>

            {selectedProductId && (
              <Row className="g-3 align-items-end mt-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Additional Notes (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Special instructions, materials, etc."
                      value={customDescription}
                      onChange={(e) => setCustomDescription(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} xs={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      min={1}
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value) || '')}
                      style={{ borderRadius: '8px' }}
                      isInvalid={!!itemErrors.quantity}
                    />
                    <Form.Control.Feedback type="invalid">{itemErrors.quantity}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2} xs={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Unit Price (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      min={0}
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(Number(e.target.value) || '')}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className="text-end">
                  <Button variant="primary" size="sm" onClick={handleAddItem} className="w-100 py-2 fw-semibold" style={{ borderRadius: '8px' }}>
                    Add
                  </Button>
                </Col>
              </Row>
            )}
          </Card>

          {/* ADDED ITEMS TABLE */}
          {items.length > 0 && (
            <Card className="border shadow-sm rounded-4 mb-4 overflow-hidden">
              <div className="bg-light px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '13px' }}>
                  <i className="fas fa-list-ul text-primary me-2"></i>Added Items ({items.length})
                </h6>
              </div>
              <Table responsive hover className="mb-0 align-middle" style={{ fontSize: '13px' }}>
                <thead className="table-light text-muted">
                  <tr>
                    <th className="fw-semibold border-0 ps-4">Product</th>
                    <th className="fw-semibold border-0">Specs / Details</th>
                    <th className="fw-semibold border-0 text-center">Qty</th>
                    <th className="fw-semibold border-0 text-center pe-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="ps-4 fw-semibold text-dark">{item.product}</td>
                      <td>
                        <div className="text-muted" style={{ fontSize: '12px' }}>
                          {item.size && <div><span className="fw-semibold">Size:</span> {item.size}</div>}
                          {item.description && <div>{item.description}</div>}
                        </div>
                      </td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-center pe-4">
                        <Button
                          variant="light"
                          size="sm"
                          className="text-danger border-0 rounded-circle"
                          style={{ width: '32px', height: '32px' }}
                          onClick={() => handleRemoveItem(idx)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          {/* Customer Details Section */}
          <div className="mb-4">
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <span className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '20px', height: '20px', fontSize: '11px' }}>2</span>
              Customer Details
            </h6>

            <div className="bg-light rounded-4 p-3 border">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '13px' }}>
                      <span>Select Profile Customer</span>
                      <Button 
                        variant="link" 
                        className="p-0 text-decoration-none fw-semibold" 
                        style={{ fontSize: '12px' }}
                        onClick={() => setShowAddCustomerModal(true)}
                      >
                        <i className="fas fa-plus me-1"></i>New Profile Customer
                      </Button>
                    </Form.Label>
                    <Form.Select
                      value={selectedCustomerId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedCustomerIdState(val);
                        const selected = customers.find((c: any) => c.id === val);
                        if (selected) {
                          setCustomerName(selected.username || '');
                          setCustomerPhone(selected.phone || '');
                          setCustomerEmail(selected.email || '');
                        } else {
                          setCustomerName('');
                          setCustomerPhone('');
                          setCustomerEmail('');
                        }
                      }}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                    >
                      <option value="">-- Select Existing Customer (or Add New) --</option>
                      {customers.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.username} {c.companyName ? `(${c.companyName})` : ''} - {c.phone || 'No phone'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Customer Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Full name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      isInvalid={!!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Phone Number *</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      isInvalid={!!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Email Address (Optional)</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="customer@example.com"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <><Spinner size="sm" animation="border" className="me-2" />Submitting...</>
            ) : (
              <><i className="fas fa-paper-plane me-2" />Log Quote Request</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
      <CustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onCustomerSaved={(savedCustomer) => {
          setSelectedCustomerIdState(savedCustomer.id);
          setCustomerName(savedCustomer.username || '');
          setCustomerPhone(savedCustomer.phone || '');
          setCustomerEmail(savedCustomer.email || '');
        }}
      />
    </Modal>
  );
};

export default CreateQuoteModal;
